/**
 * Sector + region mappings — ticker → GICS sector + region. Phase 11
 * (2026-05-09).
 *
 * Manually curated; covers the household's actual tickers + the top S&P
 * 500 names + popular ETFs (which are mapped to "FUND" sector since
 * they're a basket — sector exposure for funds is computed by the
 * holdings-overlap helper, not looked up directly).
 *
 * **Sectors** follow MSCI/S&P GICS Level-1:
 *   IT, Health Care, Financials, Communication Services, Consumer
 *   Discretionary, Consumer Staples, Industrials, Energy, Utilities,
 *   Real Estate, Materials, plus "FUND" for ETFs/MFs and "OTHER" for
 *   things we haven't classified.
 *
 * **Regions**: "US" (US-listed primary), "Developed_ex_US", "Emerging".
 * Individual ETF/MF tickers map to "FUND" — region rollup uses the
 * fund-constituent table.
 *
 * **Sources** (issuer + GICS, accessed 2026-05-09):
 *   - MSCI GICS classification: https://www.msci.com/our-solutions/indexes/gics
 *   - S&P 500 sector breakdowns: company 10-K cover pages
 *
 * **Refresh cadence**: 6 months. AS_OF below is the snapshot timestamp.
 */

export type GicsSector =
  | "IT"
  | "Health Care"
  | "Financials"
  | "Communication Services"
  | "Consumer Discretionary"
  | "Consumer Staples"
  | "Industrials"
  | "Energy"
  | "Utilities"
  | "Real Estate"
  | "Materials"
  | "FUND"
  | "OTHER";

export type Region = "US" | "Developed_ex_US" | "Emerging" | "FUND";

export interface SectorMapping {
  readonly ticker: string;
  readonly sector: GicsSector;
  readonly region: Region;
}

export const SECTOR_MAPPINGS_AS_OF = "2026-05-09";

export const SECTOR_MAPPINGS_TABLE: readonly SectorMapping[] = [
  // === Mega-cap tech (S&P top names) =====================================
  { ticker: "AAPL", sector: "IT", region: "US" },
  { ticker: "MSFT", sector: "IT", region: "US" },
  { ticker: "NVDA", sector: "IT", region: "US" },
  { ticker: "AVGO", sector: "IT", region: "US" },
  { ticker: "ORCL", sector: "IT", region: "US" },
  { ticker: "CRM", sector: "IT", region: "US" },
  { ticker: "ADBE", sector: "IT", region: "US" },
  { ticker: "AMD", sector: "IT", region: "US" },
  { ticker: "INTC", sector: "IT", region: "US" },
  { ticker: "CSCO", sector: "IT", region: "US" },
  { ticker: "ACN", sector: "IT", region: "US" },
  { ticker: "QCOM", sector: "IT", region: "US" },
  { ticker: "IBM", sector: "IT", region: "US" },
  { ticker: "TXN", sector: "IT", region: "US" },
  { ticker: "INTU", sector: "IT", region: "US" },
  { ticker: "AMAT", sector: "IT", region: "US" },
  { ticker: "MU", sector: "IT", region: "US" },
  { ticker: "NOW", sector: "IT", region: "US" },
  { ticker: "PANW", sector: "IT", region: "US" },
  { ticker: "LRCX", sector: "IT", region: "US" },
  { ticker: "KLAC", sector: "IT", region: "US" },
  { ticker: "PLTR", sector: "IT", region: "US" },
  { ticker: "SNPS", sector: "IT", region: "US" },
  { ticker: "CDNS", sector: "IT", region: "US" },

  // === Communication Services ============================================
  { ticker: "GOOGL", sector: "Communication Services", region: "US" },
  { ticker: "GOOG", sector: "Communication Services", region: "US" },
  { ticker: "META", sector: "Communication Services", region: "US" },
  { ticker: "NFLX", sector: "Communication Services", region: "US" },
  { ticker: "DIS", sector: "Communication Services", region: "US" },
  { ticker: "VZ", sector: "Communication Services", region: "US" },
  { ticker: "T", sector: "Communication Services", region: "US" },
  { ticker: "TMUS", sector: "Communication Services", region: "US" },
  { ticker: "CMCSA", sector: "Communication Services", region: "US" },

  // === Consumer Discretionary ============================================
  { ticker: "AMZN", sector: "Consumer Discretionary", region: "US" },
  { ticker: "TSLA", sector: "Consumer Discretionary", region: "US" },
  { ticker: "HD", sector: "Consumer Discretionary", region: "US" },
  { ticker: "MCD", sector: "Consumer Discretionary", region: "US" },
  { ticker: "NKE", sector: "Consumer Discretionary", region: "US" },
  { ticker: "SBUX", sector: "Consumer Discretionary", region: "US" },
  { ticker: "BKNG", sector: "Consumer Discretionary", region: "US" },
  { ticker: "LOW", sector: "Consumer Discretionary", region: "US" },
  { ticker: "TJX", sector: "Consumer Discretionary", region: "US" },
  { ticker: "ABNB", sector: "Consumer Discretionary", region: "US" },
  { ticker: "MAR", sector: "Consumer Discretionary", region: "US" },
  { ticker: "F", sector: "Consumer Discretionary", region: "US" },
  { ticker: "GM", sector: "Consumer Discretionary", region: "US" },

  // === Consumer Staples ==================================================
  { ticker: "WMT", sector: "Consumer Staples", region: "US" },
  { ticker: "PG", sector: "Consumer Staples", region: "US" },
  { ticker: "COST", sector: "Consumer Staples", region: "US" },
  { ticker: "KO", sector: "Consumer Staples", region: "US" },
  { ticker: "PEP", sector: "Consumer Staples", region: "US" },
  { ticker: "PM", sector: "Consumer Staples", region: "US" },
  { ticker: "MO", sector: "Consumer Staples", region: "US" },
  { ticker: "MDLZ", sector: "Consumer Staples", region: "US" },
  { ticker: "CL", sector: "Consumer Staples", region: "US" },
  { ticker: "TGT", sector: "Consumer Staples", region: "US" },

  // === Health Care =======================================================
  { ticker: "LLY", sector: "Health Care", region: "US" },
  { ticker: "UNH", sector: "Health Care", region: "US" },
  { ticker: "JNJ", sector: "Health Care", region: "US" },
  { ticker: "ABBV", sector: "Health Care", region: "US" },
  { ticker: "MRK", sector: "Health Care", region: "US" },
  { ticker: "PFE", sector: "Health Care", region: "US" },
  { ticker: "TMO", sector: "Health Care", region: "US" },
  { ticker: "ABT", sector: "Health Care", region: "US" },
  { ticker: "DHR", sector: "Health Care", region: "US" },
  { ticker: "AMGN", sector: "Health Care", region: "US" },
  { ticker: "ISRG", sector: "Health Care", region: "US" },
  { ticker: "BMY", sector: "Health Care", region: "US" },
  { ticker: "CVS", sector: "Health Care", region: "US" },
  { ticker: "GILD", sector: "Health Care", region: "US" },
  { ticker: "VRTX", sector: "Health Care", region: "US" },

  // === Financials ========================================================
  { ticker: "JPM", sector: "Financials", region: "US" },
  { ticker: "BAC", sector: "Financials", region: "US" },
  { ticker: "WFC", sector: "Financials", region: "US" },
  { ticker: "GS", sector: "Financials", region: "US" },
  { ticker: "MS", sector: "Financials", region: "US" },
  { ticker: "C", sector: "Financials", region: "US" },
  { ticker: "BLK", sector: "Financials", region: "US" },
  { ticker: "SCHW", sector: "Financials", region: "US" },
  { ticker: "AXP", sector: "Financials", region: "US" },
  { ticker: "BRK.B", sector: "Financials", region: "US" },
  { ticker: "BRK.A", sector: "Financials", region: "US" },
  { ticker: "V", sector: "Financials", region: "US" },
  { ticker: "MA", sector: "Financials", region: "US" },
  { ticker: "PYPL", sector: "Financials", region: "US" },
  { ticker: "SPGI", sector: "Financials", region: "US" },
  { ticker: "PNC", sector: "Financials", region: "US" },
  { ticker: "USB", sector: "Financials", region: "US" },
  { ticker: "TFC", sector: "Financials", region: "US" },
  { ticker: "COF", sector: "Financials", region: "US" },

  // === Industrials =======================================================
  { ticker: "GE", sector: "Industrials", region: "US" },
  { ticker: "HON", sector: "Industrials", region: "US" },
  { ticker: "UNP", sector: "Industrials", region: "US" },
  { ticker: "RTX", sector: "Industrials", region: "US" },
  { ticker: "LMT", sector: "Industrials", region: "US" },
  { ticker: "CAT", sector: "Industrials", region: "US" },
  { ticker: "DE", sector: "Industrials", region: "US" },
  { ticker: "BA", sector: "Industrials", region: "US" },
  { ticker: "UPS", sector: "Industrials", region: "US" },
  { ticker: "FDX", sector: "Industrials", region: "US" },
  { ticker: "GD", sector: "Industrials", region: "US" },
  { ticker: "EME", sector: "Industrials", region: "US" },
  { ticker: "ETN", sector: "Industrials", region: "US" },

  // === Energy ============================================================
  { ticker: "XOM", sector: "Energy", region: "US" },
  { ticker: "CVX", sector: "Energy", region: "US" },
  { ticker: "COP", sector: "Energy", region: "US" },
  { ticker: "EOG", sector: "Energy", region: "US" },
  { ticker: "SLB", sector: "Energy", region: "US" },
  { ticker: "PSX", sector: "Energy", region: "US" },
  { ticker: "MPC", sector: "Energy", region: "US" },
  { ticker: "VLO", sector: "Energy", region: "US" },

  // === Utilities =========================================================
  { ticker: "NEE", sector: "Utilities", region: "US" },
  { ticker: "SO", sector: "Utilities", region: "US" },
  { ticker: "DUK", sector: "Utilities", region: "US" },
  { ticker: "AEP", sector: "Utilities", region: "US" },
  { ticker: "D", sector: "Utilities", region: "US" },

  // === Real Estate =======================================================
  { ticker: "PLD", sector: "Real Estate", region: "US" },
  { ticker: "AMT", sector: "Real Estate", region: "US" },
  { ticker: "EQIX", sector: "Real Estate", region: "US" },
  { ticker: "CCI", sector: "Real Estate", region: "US" },
  { ticker: "PSA", sector: "Real Estate", region: "US" },
  { ticker: "O", sector: "Real Estate", region: "US" },
  { ticker: "SPG", sector: "Real Estate", region: "US" },

  // === Materials =========================================================
  { ticker: "LIN", sector: "Materials", region: "US" },
  { ticker: "SHW", sector: "Materials", region: "US" },
  { ticker: "FCX", sector: "Materials", region: "US" },
  { ticker: "NEM", sector: "Materials", region: "US" },
  { ticker: "DD", sector: "Materials", region: "US" },

  // === International (Developed) =========================================
  { ticker: "ASML", sector: "IT", region: "Developed_ex_US" },
  { ticker: "NESN.SW", sector: "Consumer Staples", region: "Developed_ex_US" },
  { ticker: "ROG.SW", sector: "Health Care", region: "Developed_ex_US" },
  { ticker: "NVS", sector: "Health Care", region: "Developed_ex_US" },
  { ticker: "NVO", sector: "Health Care", region: "Developed_ex_US" },
  { ticker: "AZN", sector: "Health Care", region: "Developed_ex_US" },
  { ticker: "SAP", sector: "IT", region: "Developed_ex_US" },
  { ticker: "TM", sector: "Consumer Discretionary", region: "Developed_ex_US" },
  { ticker: "SHEL", sector: "Energy", region: "Developed_ex_US" },
  { ticker: "HSBC", sector: "Financials", region: "Developed_ex_US" },
  { ticker: "TSM", sector: "IT", region: "Emerging" },
  { ticker: "BABA", sector: "Consumer Discretionary", region: "Emerging" },
  { ticker: "TCEHY", sector: "Communication Services", region: "Emerging" },
  { ticker: "PDD", sector: "Consumer Discretionary", region: "Emerging" },
  { ticker: "INFY", sector: "IT", region: "Emerging" },
  { ticker: "HDB", sector: "Financials", region: "Emerging" },
  { ticker: "VALE", sector: "Materials", region: "Emerging" },
  { ticker: "RELIANCE.NS", sector: "Energy", region: "Emerging" },
  { ticker: "HDFCBANK.NS", sector: "Financials", region: "Emerging" },
  { ticker: "MEITUAN", sector: "Consumer Discretionary", region: "Emerging" },

  // === Funds (basket) — region "FUND" ====================================
  { ticker: "VTI", sector: "FUND", region: "FUND" },
  { ticker: "VOO", sector: "FUND", region: "FUND" },
  { ticker: "VTSAX", sector: "FUND", region: "FUND" },
  { ticker: "FXAIX", sector: "FUND", region: "FUND" },
  { ticker: "FZROX", sector: "FUND", region: "FUND" },
  { ticker: "ITOT", sector: "FUND", region: "FUND" },
  { ticker: "SPY", sector: "FUND", region: "FUND" },
  { ticker: "IVV", sector: "FUND", region: "FUND" },
  { ticker: "QQQ", sector: "FUND", region: "FUND" },
  { ticker: "VUG", sector: "FUND", region: "FUND" },
  { ticker: "VTV", sector: "FUND", region: "FUND" },
  { ticker: "VB", sector: "FUND", region: "FUND" },
  { ticker: "VXUS", sector: "FUND", region: "FUND" },
  { ticker: "VTIAX", sector: "FUND", region: "FUND" },
  { ticker: "FZILX", sector: "FUND", region: "FUND" },
  { ticker: "FSPSX", sector: "FUND", region: "FUND" },
  { ticker: "VEA", sector: "FUND", region: "FUND" },
  { ticker: "VWO", sector: "FUND", region: "FUND" },
  { ticker: "BND", sector: "FUND", region: "FUND" },
  { ticker: "BNDX", sector: "FUND", region: "FUND" },
  { ticker: "AGG", sector: "FUND", region: "FUND" },
  { ticker: "VTIP", sector: "FUND", region: "FUND" },
  { ticker: "SCHB", sector: "FUND", region: "FUND" },
  { ticker: "SCHX", sector: "FUND", region: "FUND" },
  { ticker: "SCHF", sector: "FUND", region: "FUND" },
];

/** Index by ticker for O(1) lookups. */
export const SECTOR_MAPPINGS_BY_TICKER: ReadonlyMap<string, SectorMapping> = new Map(
  SECTOR_MAPPINGS_TABLE.map((m) => [m.ticker, m]),
);

/** Look up sector + region for a ticker; returns OTHER/US fallback. */
export function classifyTicker(ticker: string): SectorMapping {
  return (
    SECTOR_MAPPINGS_BY_TICKER.get(ticker) ?? {
      ticker,
      sector: "OTHER",
      region: "US",
    }
  );
}
